// src/pages/RegisterPage.jsx
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc, collection } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../firebase";
import { compressImage } from "../utils/compressImage";
import { SCHOOL } from "../config";

const SUBJECTS = ["Vocal", "Harmonium"];

const ABGMM_LEVELS = [
  "None (Beginner / No exam appeared)",
  "Prarambhik",
  "Praveshika Pratham",
  "Praveshika Purna",
  "Madhyama Pratham",
  "Madhyama Purna",
  "Visharad Pratham",
  "Visharad Purna",
  "Sangeet Alankar Pratham",
  "Sangeet Alankar Purna",
];

export default function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSibling = searchParams.get("sibling") === "true";
  const fileRef   = useRef();

  const [currentUser, setCurrentUser] = useState(auth.currentUser);


  // Photo
  const [photo,   setPhoto]   = useState(null);
  const [preview, setPreview] = useState(null);

  // Personal
  const [name,     setName]     = useState("");
  const [dob,      setDob]      = useState("");
  const [email,    setEmail]    = useState(auth.currentUser?.email || "");
  const [phone,    setPhone]    = useState("");
  const [altPhone, setAltPhone] = useState("");
  const [address,  setAddress]  = useState("");

  // Music
  const [subject,    setSubject]    = useState("");
  const [abgmmLevel, setAbgmmLevel] = useState("");
  const [note,       setNote]       = useState("");

  // Password (only for brand-new email users)
  const [password,   setPassword]   = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showPwd,    setShowPwd]    = useState(false);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  // Pre-fill from admin-created "needs_profile" doc stored in sessionStorage
  useEffect(() => {
    const raw = sessionStorage.getItem("prefillStudent");
    if (raw) {
      try {
        const d = JSON.parse(raw);
        if (d.name)  setName(d.name);
        if (d.email) setEmail(d.email);
        if (d.phone) setPhone(d.phone || "");
      } catch {}
    }
  }, []);

  // Watch Firebase auth state — handles admin-invite students completing sign-in
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setCurrentUser(u);
      if (!u) return;
      setEmail(u.email || "");

      // If already active, send straight to portal (unless adding a sibling)
      try {
        const snap = await getDoc(doc(db, "students", u.uid));
        if (snap.exists() && snap.data().status === "active" && !isSibling) {
          navigate("/", { replace: true });
          return;
        }
        // Pre-fill from admin-created "needs_profile" doc
        if (snap.exists() && snap.data().status === "needs_profile") {
          const d = snap.data();
          if (d.name  && !name)  setName(d.name);
          if (d.phone && !phone) setPhone(d.phone);
        }
        // In sibling mode, pre-fill parent's phone number
        if (isSibling && !phone && snap.exists() && snap.data().phone) {
          setPhone(snap.data().phone);
        }
      } catch {}
    });
    return unsub;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function handlePhoto(e) {
    const f = e.target.files[0]; if (!f) return;
    setPhoto(f);
    setPreview(URL.createObjectURL(f));
  }

  async function handleCancelSignIn() {
    try { await signOut(auth); } catch {}
    navigate("/login");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    // Validate
    if (!photo && !currentUser?.photoURL) { setError("Please upload a student photo."); return; }
    if (!name.trim())    { setError("Full name is required."); return; }
    if (!dob)            { setError("Date of birth is required."); return; }
    if (!phone.trim())   { setError("Mobile number is required."); return; }
    if (!address.trim()) { setError("Address is required."); return; }
    if (!email.trim())   { setError("Email is required."); return; }
    if (!subject)        { setError("Please select a subject."); return; }
    if (!abgmmLevel)     { setError("Please select your current ABGMM exam level."); return; }
    if (!currentUser) {
      if (!password)            { setError("Password is required."); return; }
      if (password.length < 6)  { setError("Password must be at least 6 characters."); return; }
      if (password !== confirmPwd) { setError("Passwords do not match."); return; }
    }

    setLoading(true);
    try {
      let user = currentUser;

      // Create Firebase Auth account if not logged in yet
      if (!user) {
        const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
        user = cred.user;
      }

      // For siblings, pre-generate a Firestore ID
      const studentDocRef = isSibling
        ? doc(collection(db, "students"))
        : doc(db, "students", user.uid);

      // Upload photo (compressed)
      let photoUrl = (!isSibling && user.photoURL) ? user.photoURL : null;
      if (photo) {
        const compressed = await compressImage(photo, { maxWidth: 800, maxHeight: 800, quality: 0.80 });
        const storageRef = ref(storage, `student_photos/${studentDocRef.id}.jpg`);
        await uploadBytes(storageRef, compressed, { contentType: 'image/jpeg' });
        photoUrl = await getDownloadURL(storageRef);
      }

      // Save student document
      await setDoc(studentDocRef, {
        uid:          user.uid,
        name:         name.trim(),
        dob,
        email:        email.trim(),
        phone:        phone.trim(),
        altPhone:     altPhone.trim(),
        address:      address.trim(),
        subject,
        abgmmLevel,
        note:         note.trim(),
        photoUrl,
        status:       "pending_review",
        courseId:     null,
        batchId:      null,
        createdAt:    new Date().toISOString(),
        registeredAt: new Date().toISOString(),
      }, isSibling ? {} : { merge: true });

      sessionStorage.removeItem("prefillStudent");
      navigate("/", { replace: true });
    } catch (err) {
      console.error("Registration error:", err.code, err.message);
      if (err.code === "auth/email-already-in-use")
        setError("An account with this email already exists. Please sign in instead.");
      else if (err.code === "storage/unauthorized")
        setError("Photo upload failed. Please try again or contact admin.");
      else
        setError(`Registration failed: ${err.message || err.code || "Please try again."}`);
    }
    setLoading(false);
  }

  const emailReadOnly = !!currentUser;

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      overflowY: "scroll", WebkitOverflowScrolling: "touch",
      background: "white",
    }}>
      <style>{`
        /* ── Layout: brand panel sticky inside scroll container ── */
        .reg-root {
          display: grid;
          grid-template-columns: 280px 1fr;
        }
        .reg-brand {
          background: linear-gradient(160deg,#4E0808,#7B1515,#9B2020);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          padding: 40px 28px;
          color: white;
          /* sticky sidebar — page scrolls, this stays */
          position: sticky;
          top: 0;
          height: 100vh;
          align-self: start;
          overflow-y: auto;
        }
        .reg-brand-detail {
          margin-top: 24px;
          border-top: 1px solid rgba(255,255,255,.2);
          padding-top: 20px;
          font-size: .8rem;
          opacity: .7;
          line-height: 2;
        }
        /* Form panel: NO overflow-y — let the document scroll naturally */
        .reg-form-panel {
          background: white;
          padding: 32px 40px 72px;
          min-height: 100vh;
        }
        .reg-section {
          font-size: .72rem;
          font-weight: 800;
          letter-spacing: .9px;
          text-transform: uppercase;
          color: #7B1515;
          margin: 22px 0 10px;
          padding-bottom: 6px;
          border-bottom: 2px solid #FBF0E8;
        }
        .reg-two { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }

        /* ── Mobile: stack vertically, page scrolls freely ── */
        @media (max-width: 768px) {
          .reg-root {
            grid-template-columns: 1fr;
          }
          .reg-brand {
            position: static;
            height: auto;
            min-height: unset;
            flex-direction: row;
            padding: 16px 20px;
            gap: 12px;
            justify-content: flex-start;
            overflow-y: visible;
          }
          .reg-brand-detail { display: none; }
          .reg-form-panel {
            padding: 24px 20px 72px;
            min-height: unset;
          }
        }
        @media (max-width: 520px) {
          .reg-two { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="reg-root">

        {/* LEFT — Branding */}
        <div className="reg-brand">
          <div style={{ width:"100%", maxWidth:"240px" }}>
            <picture>
              <source srcSet="/logo.webp" type="image/webp" />
              <img src="/logo.png" alt={SCHOOL.name}
                style={{ width:"80px", height:"80px", objectFit:"contain",
                         borderRadius:"12px", marginBottom:"14px",
                         background:"rgba(255,255,255,.1)", padding:"8px" }}/>
            </picture>
            <h1 style={{ fontSize:"1.25rem", fontWeight:"900",
                         lineHeight:1.25, marginBottom:"4px" }}>
              {SCHOOL.name}
            </h1>
            <p style={{ fontSize:".8rem", opacity:.75,
                        fontStyle:"italic", letterSpacing:".3px" }}>
              {SCHOOL.tagline}
            </p>
            <div className="reg-brand-detail">
              <div>👩‍🏫 {SCHOOL.teacher}</div>
              <div>📞 {SCHOOL.phone}</div>
              <div>📧 {SCHOOL.email}</div>
              <div>📍 {SCHOOL.address}</div>
            </div>
          </div>
        </div>

        {/* RIGHT — Form */}
        <div className="reg-form-panel">
          <div style={{ maxWidth:"560px", margin:"0 auto" }}>

            <h2 style={{ fontSize:"1.55rem", fontWeight:"800", marginBottom:"4px" }}>
              {isSibling ? "Register a Sibling" : "Student Registration"}
            </h2>
            <p style={{ color:"#6B7280", marginBottom:"20px", fontSize:".9rem" }}>
              {isSibling
                ? "Add a sibling student under your account. Each sibling has separate enrollment and billing."
                : "Complete your details. An admin will review and activate your account within 1–2 days."}
            </p>

            {error && (
              <div className="error-box" style={{ marginBottom:"16px" }}>{error}</div>
            )}

            {/* Sibling mode: require login */}
            {!currentUser && isSibling && (
              <div style={{ background:"#FEF3C7", border:"1px solid #FCD34D",
                            borderRadius:"10px", padding:"12px 14px", marginBottom:"16px",
                            fontSize:".85rem", color:"#92400E" }}>
                ⚠️ Please{" "}
                <button type="button" onClick={() => navigate("/login")}
                  style={{ background:"none", border:"none", cursor:"pointer",
                           color:"var(--purple-text)", fontWeight:"700",
                           padding:0, fontSize:".85rem" }}>
                  sign in
                </button>{" "}
                first to register a sibling.
              </div>
            )}

            {/* Already signed in (admin invite / sibling flow) — show who + allow cancel */}
            {currentUser && (
              <div style={{ background:"#F0FDF4", border:"1px solid #86EFAC",
                            borderRadius:"10px", padding:"10px 14px", marginBottom:"16px",
                            fontSize:".85rem", color:"var(--green-text)",
                            display:"flex", alignItems:"center",
                            justifyContent:"space-between", gap:"12px", flexWrap:"wrap" }}>
                <span>
                  ✅ Signed in as <strong>{currentUser.email}</strong>. Fill in your details below.
                </span>
                {!isSibling && (
                  <button type="button" onClick={handleCancelSignIn}
                    style={{ background:"none", border:"1px solid #86EFAC",
                             borderRadius:"6px", padding:"4px 10px",
                             cursor:"pointer", fontSize:".8rem",
                             color:"#374151", whiteSpace:"nowrap",
                             fontWeight:"600" }}>
                    Not you? Cancel
                  </button>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>

              {/* ── PHOTO ─────────────────────────────── */}
              <div className="reg-section">Student Photo *</div>
              <div style={{ display:"flex", alignItems:"center",
                            gap:"18px", marginBottom:"8px" }}>
                <div style={{ position:"relative", flexShrink:0 }}>
                  <div onClick={() => fileRef.current.click()}
                    style={{ width:"86px", height:"86px", borderRadius:"50%",
                             background:"#FBF0E8", border:"3px solid #F5E4D0",
                             display:"flex", alignItems:"center",
                             justifyContent:"center", fontSize:"2rem",
                             overflow:"hidden", cursor:"pointer" }}>
                    {preview
                      ? <img src={preview} alt=""
                          style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                      : currentUser?.photoURL
                        ? <img src={currentUser.photoURL} alt=""
                            style={{ width:"100%", height:"100%", objectFit:"cover" }}/>
                        : "👤"}
                  </div>
                  <button type="button" onClick={() => fileRef.current.click()}
                    style={{ position:"absolute", bottom:0, right:0,
                             width:"26px", height:"26px", borderRadius:"50%",
                             background:"#7B1515", color:"white",
                             border:"2px solid white", cursor:"pointer",
                             fontSize:"11px", display:"flex",
                             alignItems:"center", justifyContent:"center" }}>
                    📷
                  </button>
                  <input ref={fileRef} type="file" accept="image/*"
                    style={{ display:"none" }} onChange={handlePhoto}/>
                </div>
                <div style={{ fontSize:".85rem", color:"#6B7280", lineHeight:1.6 }}>
                  <div style={{ fontWeight:"600", color:"#374151" }}>
                    Upload Student Photo
                  </div>
                  <div>Clear face photo, mandatory</div>
                  <div style={{ fontSize:".78rem", color:"#9CA3AF" }}>
                    JPG/PNG · max 5 MB
                  </div>
                </div>
              </div>

              {/* ── PERSONAL DETAILS ─────────────────── */}
              <div className="reg-section">Student Details</div>

              <div className="form-group">
                <label className="form-label">Full Name *</label>
                <input className="form-input" type="text"
                  placeholder="e.g. Priya Sharma"
                  value={name} onChange={e => setName(e.target.value)} required/>
              </div>

              <div className="reg-two">
                <div className="form-group">
                  <label className="form-label">Date of Birth *</label>
                  <input className="form-input" type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={dob} onChange={e => setDob(e.target.value)} required/>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Email *
                    {emailReadOnly && (
                      <span style={{ fontWeight:"400", fontSize:".75rem",
                                     color:"#9CA3AF", marginLeft:"6px" }}>
                        (from sign-in)
                      </span>
                    )}
                  </label>
                  <input className="form-input" type="email"
                    placeholder="email@example.com"
                    value={email} onChange={e => setEmail(e.target.value)}
                    readOnly={emailReadOnly}
                    style={emailReadOnly
                      ? { background:"#F9FAFB", color:"#6B7280" }
                      : {}}
                    required/>
                </div>
              </div>

              <div className="reg-two">
                <div className="form-group">
                  <label className="form-label">
                    Mobile Number *
                    <span style={{ fontWeight:"400", fontSize:".75rem",
                                   color:"#9CA3AF", marginLeft:"4px" }}>
                      (student or parent)
                    </span>
                  </label>
                  <input className="form-input" type="tel"
                    placeholder="98765 43210"
                    value={phone} onChange={e => setPhone(e.target.value)} required/>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Alternate Mobile
                    <span style={{ fontWeight:"400", fontSize:".75rem",
                                   color:"#9CA3AF", marginLeft:"4px" }}>
                      (optional)
                    </span>
                  </label>
                  <input className="form-input" type="tel"
                    placeholder="alternate number"
                    value={altPhone} onChange={e => setAltPhone(e.target.value)}/>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Address *</label>
                <textarea className="form-input" rows={2}
                  placeholder="Flat / House No., Street, Area, City, PIN code"
                  value={address} onChange={e => setAddress(e.target.value)}
                  required style={{ resize:"vertical", minHeight:"60px" }}/>
              </div>

              {/* ── MUSIC DETAILS ─────────────────────── */}
              <div className="reg-section">Music Details</div>

              <div className="reg-two">
                <div className="form-group">
                  <label className="form-label">Subject *</label>
                  <select className="form-input" value={subject}
                    onChange={e => setSubject(e.target.value)} required>
                    <option value="">— Select Subject —</option>
                    {SUBJECTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">ABGMM Exam History *</label>
                  <select className="form-input" value={abgmmLevel}
                    onChange={e => setAbgmmLevel(e.target.value)} required>
                    <option value="">— Current Level —</option>
                    {ABGMM_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">
                  Additional Notes
                  <span style={{ fontWeight:"400", fontSize:".75rem",
                                 color:"#9CA3AF", marginLeft:"4px" }}>
                    (optional)
                  </span>
                </label>
                <textarea className="form-input" rows={2}
                  placeholder="Learning goals, previous training, preferred batch timings…"
                  value={note} onChange={e => setNote(e.target.value)}
                  style={{ resize:"vertical" }}/>
              </div>

              {/* ── PASSWORD — only for brand-new email users ── */}
              {!currentUser && (
                <>
                  <div className="reg-section">Account Password</div>
                  <div className="reg-two">
                    <div className="form-group">
                      <label className="form-label">Password *</label>
                      <div style={{ position:"relative" }}>
                        <input className="form-input"
                          type={showPwd ? "text" : "password"}
                          placeholder="Min 6 characters"
                          value={password}
                          onChange={e => setPassword(e.target.value)}
                          required
                          style={{ paddingRight:"44px" }}/>
                        <button type="button" onClick={() => setShowPwd(!showPwd)}
                          style={{ position:"absolute", right:"12px", top:"50%",
                                   transform:"translateY(-50%)", background:"none",
                                   border:"none", cursor:"pointer",
                                   fontSize:"1rem", color:"#6B7280" }}>
                          {showPwd ? "🙈" : "👁️"}
                        </button>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Confirm Password *</label>
                      <input className="form-input"
                        type={showPwd ? "text" : "password"}
                        placeholder="Repeat password"
                        value={confirmPwd}
                        onChange={e => setConfirmPwd(e.target.value)}
                        required/>
                    </div>
                  </div>
                </>
              )}

              <button className="btn btn-primary" type="submit"
                disabled={loading}
                style={{ width:"100%", padding:"13px", fontSize:"1rem",
                         borderRadius:"10px", marginTop:"8px" }}>
                {loading ? <span className="spinner"/> : "Submit Registration"}
              </button>

              {!isSibling && (
                <p style={{ textAlign:"center", marginTop:"16px",
                            fontSize:".88rem", color:"#6B7280" }}>
                  Already have an account?{" "}
                  <button type="button" onClick={() => navigate("/login")}
                    style={{ background:"none", border:"none", cursor:"pointer",
                             color:"var(--purple-text)", fontWeight:"700",
                             fontSize:".88rem", padding:0 }}>
                    Sign in
                  </button>
                </p>
              )}
              {isSibling && (
                <p style={{ textAlign:"center", marginTop:"16px",
                            fontSize:".88rem", color:"#6B7280" }}>
                  <button type="button" onClick={() => navigate("/")}
                    style={{ background:"none", border:"none", cursor:"pointer",
                             color:"var(--purple-text)", fontWeight:"700",
                             fontSize:".88rem", padding:0 }}>
                    ← Back to portal
                  </button>
                </p>
              )}

            </form>
          </div>
        </div>

      </div>
    </div>
  );
}
